/** How this site works.
 *
 * The client asked for "a help section to explain the website functionality /
 * menus / items / FAQs" and, separately, to "explain how to use the website
 * for learning". Those are two different questions and the page answers them
 * in that order: first what to DO (a path through the product), then what
 * each menu IS, then the things people actually ask.
 *
 * The menu descriptions are the tour's own copy (`BODY_COPY`), not a second
 * set written here. Two explanations of one menu that disagree would be
 * worse than either alone, and the tour's version is already type-enforced
 * to cover every item. Only the viewer's OWN menus are listed — a learner
 * reading about the Access screen learns nothing except that the product is
 * bigger than their part of it.
 */
import { useState } from "react";
import { Card } from "../../components/ui";
import { navItemsFor } from "../../components/layout/AppShell";
import { BODY_COPY } from "../../components/tour/tourSteps";
import { useAuth } from "../../auth/AuthContext";
import { useT } from "../../i18n";

/** The order a learner actually moves through the product. */
const STEPS = ["learn", "practice", "speak", "ask", "track"] as const;

/** Questions we have really been asked, or that the feedback implies. */
const FAQS = [
  "credits", "voice", "offline", "level", "exam", "notes", "history", "mobile",
] as const;

export function HelpPage() {
  const t = useT();
  const { user } = useAuth();
  const [openFaq, setOpenFaq] = useState<string | null>(null);

  const menus = navItemsFor(user?.role, { solo: user?.is_self_serve });

  return (
    <div className="space-y-4 sm:space-y-6">
      <div>
        <h1 className="text-2xl font-extrabold text-ink">{t("help.title")}</h1>
        <p className="mt-1 text-sm text-muted">{t("help.subtitle")}</p>
      </div>

      <Card title={t("help.howTo")}>
        <p className="text-sm text-muted">{t("help.howToIntro")}</p>
        <ol className="mt-3 space-y-3">
          {STEPS.map((step, i) => (
            <li key={step} className="flex gap-3">
              <span
                aria-hidden
                className="grid h-6 w-6 shrink-0 place-items-center rounded-full bg-brand-soft text-xs font-bold text-brand-dark"
              >
                {i + 1}
              </span>
              <div className="min-w-0">
                <p className="text-sm font-bold text-ink">
                  {t(`help.step.${step}.title`)}
                </p>
                <p className="text-sm text-muted">{t(`help.step.${step}.body`)}</p>
              </div>
            </li>
          ))}
        </ol>
      </Card>

      <Card title={t("help.menus")}>
        <p className="text-sm text-muted">{t("help.menusIntro")}</p>
        <dl className="mt-3 space-y-3" data-testid="menu-guide">
          {menus.map((item) => (
            <div key={item.key}>
              <dt className="text-sm font-bold text-ink">{t(item.key)}</dt>
              <dd className="text-sm text-muted">{t(BODY_COPY[item.key])}</dd>
            </div>
          ))}
        </dl>
      </Card>

      <Card title={t("help.faq")}>
        <ul className="divide-y divide-line">
          {FAQS.map((faq) => (
            <li key={faq}>
              <button
                type="button"
                onClick={() => setOpenFaq(openFaq === faq ? null : faq)}
                aria-expanded={openFaq === faq}
                className="flex w-full flex-wrap items-center justify-between gap-2 py-3 text-left"
              >
                <span className="text-sm font-semibold text-ink">
                  {t(`help.faq.${faq}.q`)}
                </span>
                <span aria-hidden className="text-muted">
                  {openFaq === faq ? "−" : "+"}
                </span>
              </button>
              {openFaq === faq && (
                <p className="pb-3 text-sm text-muted">{t(`help.faq.${faq}.a`)}</p>
              )}
            </li>
          ))}
        </ul>
      </Card>
    </div>
  );
}
